import * as fs from 'fs';
import * as path from 'path';
import * as crypto from 'crypto';
import type {
  Team,
  TeamMessage,
  TeamFeedItem,
  TeamPresenceEntry,
  HoloMeshCloudSessionLease,
  RegisteredAgent,
  StoredComment,
  StoredVote,
  StoredBountySubmission,
  StoredBountyMiniGame,
  StoredBountyGovernanceProposal,
  StoryWeaverSession,
  SelfImprovingWorldSession,
  KnowledgeTransaction,
  KeyRecord,
  ExportSession,
} from './types';
import { BountyManager, KnowledgeMarketplace } from '@holoscript/framework';
import {
  deserializeExportSession,
  isExportSessionExpired,
  serializeExportSession,
} from './export-session';
import { serializeLedger, deserializeLedger } from './identity/token-ledger';
import { createTeamStore, type TeamStore } from './team-store';
import { createStateStore, type StateStoreBackend } from './state-store';
import { createPlayerStore, type PlayerStore, type StoredPlayer } from './player-store';
import { createInviteStore, type InviteStore } from './invite-store';
import { createGeoAnchorStore, type GeoAnchorStore } from './geo-anchor-store';

// ── Persistence Config ────────────────────────────────────────────────────────

export const HOLOMESH_DATA_DIR =
  process.env.HOLOMESH_DATA_DIR ||
  path.join(
    process.env.HOLOSCRIPT_CACHE_DIR || path.join(require('os').homedir(), '.holoscript'),
    'holomesh'
  );

// ── Atomic Writes ─────────────────────────────────────────────────────────────

/** Atomic JSON write: temp file → rename */
export function atomicWriteJSON(filePath: string, data: unknown): void {
  try {
    const dir = path.dirname(filePath);
    if (!fs.existsSync(dir)) fs.mkdirSync(dir, { recursive: true });
    const tmp = filePath + '.tmp';
    fs.writeFileSync(tmp, JSON.stringify(data, null, 2), 'utf-8');
    fs.renameSync(tmp, filePath);
  } catch (e: unknown) {
    console.warn('[HoloMesh] persist failed:', e instanceof Error ? e.message : String(e));
  }
}

/** Read JSON file, return null if missing/corrupted */
export function readJSON(filePath: string): any {
  try {
    if (!fs.existsSync(filePath)) return null;
    return JSON.parse(fs.readFileSync(filePath, 'utf-8'));
  } catch {
    return null;
  }
}

// ── Shared Global Stores ──────────────────────────────────────────────────────

// Identity
export const agentKeyStore: Map<string, RegisteredAgent> = new Map(); // apiKey → agent
export const walletToAgent: Map<string, RegisteredAgent> = new Map(); // walletAddress (lowercase) → agent
export const paidAccessStore: Set<string> = new Set(); // "agentId:entryId" → true

// Teams
export const teamStore: TeamStore = createTeamStore(); // teamId → Team

// Players (HoloLand users — Postgres-backed so agents never see an empty list)
export const playerStore: PlayerStore = createPlayerStore(); // playerId → StoredPlayer

// Agent-first invite tokens — claimed by humans to create their player account
export const inviteStore: InviteStore = createInviteStore(); // token → InviteRecord
export const geoAnchorStore: GeoAnchorStore = createGeoAnchorStore(); // anchorId -> StoredGeoAnchor
export const stateStore: StateStoreBackend = createStateStore(); // audit/defense/dispatch backend
export const teamCloudSessionStore: Map<string, Map<string, HoloMeshCloudSessionLease>> =
  new Map(); // teamId -> (leaseId -> cloud session lease)
export const teamPresenceStore: Map<string, Map<string, TeamPresenceEntry>> = new Map(); // teamId → (agentId → presence)
export const teamMessageStore: Map<string, TeamMessage[]> = new Map(); // teamId → messages
/** Public team feed (hologram publishes, etc.) — poster identity is server-authoritative */
export const teamFeedStore: Map<string, TeamFeedItem[]> = new Map();
export const agentTeamIndex: Map<string, string[]> = new Map(); // agentId → teamIds[]

const MAX_TEAM_FEED_ITEMS = 200;

/** HoloDoor — per-team merged policy (owner PATCH) + telemetry ring buffer */
export const holoDoorPolicyByTeam: Map<string, Record<string, unknown>> = new Map();
export const holoDoorEventsByTeam: Map<string, Record<string, unknown>[]> = new Map();

/**
 * CAEL Audit Store — per-agent CAEL trace ring buffer (Phase 0 in-memory).
 * Spec: research/2026-04-25_fleet-adversarial-harness-paper-21.md +
 *       research/2026-04-25_fleet-empirical-composability-w-gold-189.md.
 * Closes gap-build task_1777090894117_d2jx (CAEL audit GET endpoint).
 *
 * Each entry is one CAEL record produced by an attacker-loop / target-brain /
 * composability-test capture. Records have shape per spec §5.
 *
 * Phase 0 limitations (filed as follow-up at task_1777093147560_pawd):
 * - In-memory only; lost on server restart. Persistence to JSONL on
 *   HOLOMESH_DATA_DIR is the Phase 1 hardening pass.
 * - Per-agent ring buffer caps at 10,000 records (drops oldest).
 * - No pagination cursor; only since/until/limit windowing.
 *
 * Auth model: write requires the caller to BE the agent (bearer matches
 * handle); read requires any authenticated agent in the same team.
 */
export interface CaelAuditRecord {
  tick_iso: string;
  layer_hashes: string[]; // 7 elements per W.GOLD.189 Layer 1+2 framing
  operation: string;
  prev_hash: string | null;
  fnv1a_chain: string;
  version_vector_fingerprint: string;
  // Optional: attacker/composability-test specific metadata
  attack_class?: string;
  defense_state?: string;
  trial?: number;
  brain_class?: string;
  // W.110 trust epoch — set by post-W.107.b runners (HS commit 435928977).
  // Records lacking this field are pre-gate (mw02 hallucination cohort) and
  // are implicitly untrusted. Production counts (Paper 17/19/21/25 corpus,
  // fleet-status drift detector) MUST filter via isCaelRecordTrusted().
  // Canonical source: ai-ecosystem/scripts/lib/trust-epoch.mjs.
  trust_epoch?: 'post-w107';
  // Server-stamped on append
  received_at: string;
}

/** Trust-epoch tag set by post-W.107.b runners. Pre-gate records lack this. */
export const CAEL_TRUST_EPOCH_POSTGATE = 'post-w107' as const;

/**
 * Strict trust-epoch check — mirrors `isTrusted()` in
 * `ai-ecosystem/scripts/lib/trust-epoch.mjs`. Returns true iff the record
 * carries the explicit post-W.107.b tag. Required filter for any caller
 * that draws scientific or operational conclusions from CAEL records.
 *
 * The two fleet-status counter loops in `routes/core-routes.ts` (the team
 * fleet-status endpoint at ~line 681 and the composite /fleet/status at
 * ~line 901) MUST gate on this — otherwise pre-W.107 hallucinations
 * contaminate metrics and trigger drift-detection false positives.
 */
export function isCaelRecordTrusted(record: CaelAuditRecord): boolean {
  return record.trust_epoch === CAEL_TRUST_EPOCH_POSTGATE;
}

export const agentAuditStore: Map<string, CaelAuditRecord[]> = new Map(); // handle → records
const MAX_CAEL_RECORDS_PER_AGENT = 10_000;

// Phase 1 persistence: append every record to JSONL on disk.
// Path: <HOLOMESH_DATA_DIR>/audit/<handle>.jsonl
// On startup, loadCaelAuditFromDisk() rehydrates the in-memory store.
const CAEL_AUDIT_DIR = path.join(HOLOMESH_DATA_DIR, 'audit');

function caelAuditPath(handle: string): string {
  // Sanitize handle for filesystem (no ../, no path separators).
  const safe = handle.replace(/[^a-zA-Z0-9._-]/g, '_');
  return path.join(CAEL_AUDIT_DIR, `${safe}.jsonl`);
}

function persistCaelAuditRecord(handle: string, record: CaelAuditRecord): void {
  if (
    process.env.NODE_ENV === 'test' &&
    process.env.HOLOMESH_TEST_DISABLE_CAEL_AUDIT_PERSISTENCE === '1'
  ) {
    return;
  }

  try {
    if (!fs.existsSync(CAEL_AUDIT_DIR)) {
      fs.mkdirSync(CAEL_AUDIT_DIR, { recursive: true });
    }
    fs.appendFileSync(caelAuditPath(handle), `${JSON.stringify(record)}\n`, 'utf8');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[state] persistCaelAuditRecord failed for ${handle}: ${(err as Error).message}`);
  }
  // PostgreSQL backend: fire-and-forget append
  stateStore.append('audit', handle, record).catch((e) => {
    console.warn(`[state] stateStore audit append failed for ${handle}:`, e);
  });
}

/**
 * Rehydrate in-memory CAEL audit store from on-disk JSONL files.
 * Called once at server startup. Idempotent — safe to call multiple times.
 */
export function loadCaelAuditFromDisk(): void {
  if (!fs.existsSync(CAEL_AUDIT_DIR)) return;
  const files = fs.readdirSync(CAEL_AUDIT_DIR).filter((f) => f.endsWith('.jsonl'));
  for (const file of files) {
    const handle = file.replace(/\.jsonl$/, '');
    const lines = fs
      .readFileSync(path.join(CAEL_AUDIT_DIR, file), 'utf8')
      .split('\n')
      .filter((l) => l.length > 0);
    const records: CaelAuditRecord[] = [];
    for (const line of lines) {
      try {
        records.push(JSON.parse(line));
      } catch {
        // Corrupt line — skip. Honest: a half-flushed record is rare.
      }
    }
    // Honor the in-memory ring buffer cap on rehydrate.
    const trimmed =
      records.length > MAX_CAEL_RECORDS_PER_AGENT
        ? records.slice(-MAX_CAEL_RECORDS_PER_AGENT)
        : records;
    agentAuditStore.set(handle, trimmed);
  }
}

/**
 * Agent Defense State Store — per-agent fleet-adversarial defense configuration.
 * Closes gap-build task_1777090894117_8bav (defense-state PATCH endpoint).
 * Spec: research/2026-04-25_fleet-adversarial-harness-paper-21.md §3.
 *
 * Allows the adversarial harness to toggle each target's defense state
 * between {none, decay-on-anomaly, cross-vouching-detector, replay-audit,
 * canary-probing, all-three}. Phase 1 of the harness (10-cell smoke) and
 * Phase 2 (300/450 cell full eval) require this for the per-cell
 * defense_state matrix dimension.
 *
 * Phase 1 hardening landed (this commit): persistence to disk via
 * <HOLOMESH_DATA_DIR>/defense/<handle>.json.
 *
 * Remaining limitations (Phase 1.5, filed at task_1777093147560_pawd):
 * - Auth is "any authenticated caller". Phase 1.5 will require caller's
 *   brain class (via /me + agents.json lookup) to be security-auditor.
 * - Defense state is informational — doesn't actually toggle behavior on
 *   the target brain (target brains read this on next tick).
 */
export type DefenseState =
  | 'none'
  | 'decay-on-anomaly'
  | 'cross-vouching-detector'
  | 'replay-audit'
  | 'canary-probing'
  | 'all-three';

export const VALID_DEFENSE_STATES: readonly DefenseState[] = [
  'none',
  'decay-on-anomaly',
  'cross-vouching-detector',
  'replay-audit',
  'canary-probing',
  'all-three',
];

export interface AgentDefenseConfig {
  state: DefenseState;
  expiresAt: string | null; // ISO; null = no expiry
  setBy: string; // agentId of caller who set this
  setAt: string; // ISO server-stamp
}

export const agentDefenseStore: Map<string, AgentDefenseConfig> = new Map(); // handle → config

// Phase 1 persistence: write defense state to JSON on disk.
const DEFENSE_DIR = path.join(HOLOMESH_DATA_DIR, 'defense');

function defensePath(handle: string): string {
  const safe = handle.replace(/[^a-zA-Z0-9._-]/g, '_');
  return path.join(DEFENSE_DIR, `${safe}.json`);
}

function persistAgentDefense(handle: string, config: AgentDefenseConfig): void {
  try {
    if (!fs.existsSync(DEFENSE_DIR)) {
      fs.mkdirSync(DEFENSE_DIR, { recursive: true });
    }
    fs.writeFileSync(defensePath(handle), JSON.stringify(config, null, 2), 'utf8');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[state] persistAgentDefense failed for ${handle}: ${(err as Error).message}`);
  }
  // PostgreSQL backend: fire-and-forget overwrite
  stateStore.set('defense', handle, config).catch((e) => {
    console.warn(`[state] stateStore defense set failed for ${handle}:`, e);
  });
}

/**
 * Rehydrate in-memory defense store from on-disk JSON files.
 * Called once at server startup. Idempotent.
 */
export function loadAgentDefenseFromDisk(): void {
  if (!fs.existsSync(DEFENSE_DIR)) return;
  const files = fs.readdirSync(DEFENSE_DIR).filter((f) => f.endsWith('.json'));
  for (const file of files) {
    const handle = file.replace(/\.json$/, '');
    try {
      const config = JSON.parse(
        fs.readFileSync(path.join(DEFENSE_DIR, file), 'utf8')
      ) as AgentDefenseConfig;
      agentDefenseStore.set(handle, config);
    } catch {
      // Corrupt file — skip.
    }
  }
}

export function setAgentDefense(
  handle: string,
  state: DefenseState,
  expiresAt: string | null,
  setBy: string
): AgentDefenseConfig {
  const config: AgentDefenseConfig = {
    state,
    expiresAt,
    setBy,
    setAt: new Date().toISOString(),
  };
  agentDefenseStore.set(handle, config);
  // Phase 1 persistence: best-effort write to JSON.
  persistAgentDefense(handle, config);
  return config;
}

export function getAgentDefense(handle: string): AgentDefenseConfig | null {
  const config = agentDefenseStore.get(handle);
  if (!config) return null;
  // Honor expiry — if expired, return 'none' implicitly without touching store
  if (config.expiresAt && Date.parse(config.expiresAt) < Date.now()) {
    return null;
  }
  return config;
}

export function isValidDefenseState(value: unknown): value is DefenseState {
  return typeof value === 'string' && (VALID_DEFENSE_STATES as readonly string[]).includes(value);
}

/**
 * Adversarial Dispatch Queue — pull-based trigger for headless attacker
 * brains running on Vast.ai boxes. Closes the last gap at task_..._pawd:
 * how does run-harness.mjs (the coordinator) trigger an attacker loop on
 * a remote worker?
 *
 * Design (Phase 1):
 *   - Coordinator POSTs to /api/holomesh/agent/:handle/dispatch with the
 *     trial cell parameters (attack_class, target_handle, duration_ms,
 *     defense_state, trial). Server enqueues to agentDispatchQueue.
 *   - Worker brain polls GET /api/holomesh/agent/:handle/dispatch (auth:
 *     handle owner only). Server returns pending dispatches AND drains
 *     them from the queue (one-shot delivery).
 *   - Worker invokes its attacker loop with the cell parameters.
 *   - Worker writes CAEL records via existing POST /audit endpoint.
 *
 * Auth:
 *   - POST: founder or HOLOMESH_SECURITY_AUDITOR_HANDLES (admin-class).
 *   - GET: handle owner only (caller.name === pathHandle) or founder.
 *
 * Per-handle ring buffer: cap 1000 entries; drop oldest. Persists to
 * <HOLOMESH_DATA_DIR>/dispatch/<handle>.jsonl.
 */
export const VALID_ATTACK_CLASSES = [
  'whitewasher',
  'sybil-cross-vouch',
  'slow-poisoner',
  'reputation-squatter',
  'cross-brain-hijack',
] as const;

export type AttackClass = (typeof VALID_ATTACK_CLASSES)[number];

export interface DispatchEntry {
  cell_id: string; // run-harness assigned, e.g. "phase-2-A-23"
  attack_class: AttackClass;
  target_handle: string;
  duration_ms: number;
  trial: number;
  defense_state: DefenseState;
  dispatched_at: string;
  dispatched_by: string; // caller agentId
  // Server-stamped on consume
  consumed_at?: string;
}

export const agentDispatchQueue: Map<string, DispatchEntry[]> = new Map(); // handle → pending entries
const MAX_DISPATCH_QUEUE_PER_AGENT = 1000;
const DISPATCH_DIR = path.join(HOLOMESH_DATA_DIR, 'dispatch');

function dispatchPath(handle: string): string {
  const safe = handle.replace(/[^a-zA-Z0-9._-]/g, '_');
  return path.join(DISPATCH_DIR, `${safe}.jsonl`);
}

function persistDispatch(handle: string, entry: DispatchEntry): void {
  try {
    if (!fs.existsSync(DISPATCH_DIR)) {
      fs.mkdirSync(DISPATCH_DIR, { recursive: true });
    }
    fs.appendFileSync(dispatchPath(handle), `${JSON.stringify(entry)}\n`, 'utf8');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[state] persistDispatch failed for ${handle}: ${(err as Error).message}`);
  }
  // PostgreSQL backend: fire-and-forget append
  stateStore.append('dispatch', handle, entry).catch((e) => {
    console.warn(`[state] stateStore dispatch append failed for ${handle}:`, e);
  });
}

export function isValidAttackClass(value: unknown): value is AttackClass {
  return typeof value === 'string' && (VALID_ATTACK_CLASSES as readonly string[]).includes(value);
}

export function enqueueDispatch(handle: string, entry: DispatchEntry): DispatchEntry {
  const existing = agentDispatchQueue.get(handle) ?? [];
  existing.push(entry);
  if (existing.length > MAX_DISPATCH_QUEUE_PER_AGENT) {
    existing.shift();
  }
  agentDispatchQueue.set(handle, existing);
  persistDispatch(handle, entry);
  return entry;
}

/**
 * Drain pending dispatches for a handle. Returns and removes all
 * pending entries (one-shot delivery — the worker is responsible for
 * executing or re-enqueueing if it can't process them now).
 */
export function consumeDispatches(handle: string): DispatchEntry[] {
  const pending = agentDispatchQueue.get(handle) ?? [];
  if (pending.length === 0) return [];
  const consumedAt = new Date().toISOString();
  const drained = pending.map((e) => ({ ...e, consumed_at: consumedAt }));
  agentDispatchQueue.set(handle, []);
  // Persist consumption events as a sidecar (audit trail).
  for (const entry of drained) {
    persistDispatch(handle, entry);
  }
  return drained;
}

/**
 * Read pending dispatches without draining (for diagnostic / observability).
 */
export function peekDispatches(handle: string): DispatchEntry[] {
  return [...(agentDispatchQueue.get(handle) ?? [])];
}

export function appendCaelAuditRecord(handle: string, record: CaelAuditRecord): void {
  const existing = agentAuditStore.get(handle) ?? [];
  existing.push(record);
  if (existing.length > MAX_CAEL_RECORDS_PER_AGENT) {
    existing.shift(); // ring buffer: drop oldest
  }
  agentAuditStore.set(handle, existing);
  // Phase 1 persistence: best-effort append to JSONL.
  persistCaelAuditRecord(handle, record);
}

export function queryCaelAuditRecords(
  handle: string,
  filter: {
    since?: string;
    until?: string;
    limit?: number;
    operation?: string;
    /** W.110: when true, drop records lacking `trust_epoch === 'post-w107'`.
     *  Default `false` preserves the historical full-record behavior so the
     *  forensic audit endpoint keeps returning everything. Production count
     *  callers (fleet-status, paper-corpus consumers) should pass `true`. */
    trustedOnly?: boolean;
  } = {}
): CaelAuditRecord[] {
  const records = agentAuditStore.get(handle) ?? [];
  let out = records;
  if (filter.trustedOnly) {
    out = out.filter(isCaelRecordTrusted);
  }
  if (filter.since) {
    const sinceMs = Date.parse(filter.since);
    if (Number.isFinite(sinceMs)) {
      out = out.filter((r) => Date.parse(r.tick_iso) >= sinceMs);
    }
  }
  if (filter.until) {
    const untilMs = Date.parse(filter.until);
    if (Number.isFinite(untilMs)) {
      out = out.filter((r) => Date.parse(r.tick_iso) <= untilMs);
    }
  }
  if (filter.operation) {
    out = out.filter((r) => r.operation === filter.operation);
  }
  const limit = filter.limit ?? 1000;
  if (out.length > limit) out = out.slice(out.length - limit); // most recent
  return out;
}

// Social & Discussion
export const commentStore: Map<string, StoredComment[]> = new Map(); // entryId → comments
export const voteStore: Map<string, StoredVote[]> = new Map(); // targetId → votes
export const transactionLedger: KnowledgeTransaction[] = [];
export const storyWeaverStore: Map<string, StoryWeaverSession> = new Map(); // sessionId -> story session
export const selfImprovingWorldStore: Map<string, SelfImprovingWorldSession> = new Map(); // worldId -> world evolution session

// Bounties
export const bountySubmissionStore: Map<string, StoredBountySubmission[]> = new Map(); // bountyId -> submissions
export const bountyMiniGameStore: Map<string, StoredBountyMiniGame[]> = new Map(); // teamId -> mini-games
export const bountyGovernanceStore: Map<string, StoredBountyGovernanceProposal> = new Map(); // bountyId -> proposal

// Auth
export const challengeStore: Map<string, { walletAddress: string; expiresAt: number }> = new Map();

// Key Registry — unified identity anchor
export const keyRegistry: Map<string, KeyRecord> = new Map(); // token → KeyRecord

// Admin Ops State (P.009.01 — ephemeral; audit trail is the durable record)
export interface ScalingOverride {
  serviceId: string;
  replicas: number;
  reason?: string;
  setAt: string;
  setBy: string;
}
export interface FailoverState {
  serviceId: string;
  primaryBackend: string;
  reason?: string;
  setAt: string;
  setBy: string;
}
export const scalingOverrideStore: Map<string, ScalingOverride> = new Map(); // serviceId → override
export const failoverStateStore: Map<string, FailoverState> = new Map(); // serviceId → state

// Tier2 Self-Custody Export Sessions (V3 foundation)
export const exportSessionStore: Map<string, ExportSession> = new Map(); // sessionId → export session

// ── Persistence Logic ─────────────────────────────────────────────────────────

const TEAM_STORE_PATH = path.join(HOLOMESH_DATA_DIR, 'teams.json');
const AGENT_STORE_PATH = path.join(HOLOMESH_DATA_DIR, 'agents.json');
const SOCIAL_STORE_PATH = path.join(HOLOMESH_DATA_DIR, 'social.json');
const KEY_REGISTRY_PATH = path.join(HOLOMESH_DATA_DIR, 'keys.json');
const HOLODOOR_STORE_PATH = path.join(HOLOMESH_DATA_DIR, 'holodoor-store.json');
const EXPORT_SESSION_STORE_PATH = path.join(HOLOMESH_DATA_DIR, 'export-sessions.json');
const TOKEN_LEDGER_PATH = path.join(HOLOMESH_DATA_DIR, 'token-ledger.json');

export function persistHoloDoorStore(): void {
  atomicWriteJSON(HOLODOOR_STORE_PATH, {
    version: 1,
    policies: Object.fromEntries(holoDoorPolicyByTeam),
    events: Object.fromEntries(holoDoorEventsByTeam),
    savedAt: new Date().toISOString(),
  });
}

/** Refresh a single team from the shared backend (PostgreSQL) into local cache.
 *  Call at the start of mutation handlers so cross-instance writes are visible.
 */
export async function reloadTeam(teamId: string): Promise<void> {
  await (teamStore as TeamStore).getFresh(teamId);
}

export function persistTeamStore(): void {
  // Pattern Gamma write-path fix (2026-05-04): when Postgres is active,
  // route handlers mutate cached Team objects in place (e.g. team.taskBoard.push)
  // and rely on this function to persist. Early-returning was a silent no-op —
  // mutations never reached Postgres, so other Railway replicas saw stale state
  // and writes appeared to "vanish" minutes later when load-balancer routing
  // moved to a different replica. teamStore.set() fires the write-through.
  if ((teamStore as TeamStore).usesPostgres) {
    for (const team of teamStore.values()) {
      teamStore.set(team.id, team);
    }
    return;
  }

  const teams = Array.from(teamStore.values()).map((t) => ({
    ...t,
    presence: teamPresenceStore.get(t.id) ? Array.from(teamPresenceStore.get(t.id)!.values()) : [],
    messages: teamMessageStore.get(t.id) || [],
    feed: teamFeedStore.get(t.id) || [],
    knowledgeMarketplace:
      (t as any).knowledgeMarketplace?.toJSON?.() || (t as any).knowledgeMarketplace,
    bounties: (t as any).bounties?.toJSON?.() || t.bounties,
  }));

  atomicWriteJSON(TEAM_STORE_PATH, {
    version: 4,
    teams,
    savedAt: new Date().toISOString(),
  });
}

/**
 * Durably persist a single team and AWAIT the write (W.128 fix, 2026-06-16).
 *
 * `persistTeamStore()` routes through `teamStore.set()`, whose Postgres
 * write-through is FIRE-AND-FORGET (team-store.ts:190). Membership mutations
 * (join / set_role / remove) returned 200 *before* the row committed, so a
 * fast follow-up request that `reloadTeam()`s (e.g. the AgentRunner heartbeat
 * → /presence immediately after a join) read stale Postgres and 403'd
 * "Not a member" — the seat appeared to never join (W.128 / Pattern Gamma).
 *
 * Membership handlers must AWAIT this instead of calling the fire-and-forget
 * `persistTeamStore()`, so the row is durable before the response. On the
 * in-memory backend (single-instance dev) there is no race, so we fall back to
 * the synchronous file write.
 */
export async function persistTeamDurable(teamId: string): Promise<void> {
  if (teamStore.usesPostgres) {
    await teamStore.persist(teamId);
  } else {
    persistTeamStore();
  }
}

export function persistAgentStore(): void {
  atomicWriteJSON(AGENT_STORE_PATH, {
    version: 1,
    agents: Array.from(agentKeyStore.values()),
    savedAt: new Date().toISOString(),
  });
}

/**
 * keys.json is append-only — read existing records, merge new ones, write.
 * Old keys are preserved for audit trail. Rotated keys are never truly deleted
 * from the file (only from the in-memory map).
 */
export function persistKeyRegistry(): void {
  const existing = readJSON(KEY_REGISTRY_PATH);
  const existingRecords: KeyRecord[] = existing?.keys || [];
  const merged = new Map<string, KeyRecord>();
  for (const r of existingRecords) merged.set(r.key, r);
  for (const r of keyRegistry.values()) merged.set(r.key, r);
  atomicWriteJSON(KEY_REGISTRY_PATH, {
    version: 1,
    keys: Array.from(merged.values()),
    savedAt: new Date().toISOString(),
  });
}

export function persistSocialStore(): void {
  atomicWriteJSON(SOCIAL_STORE_PATH, {
    version: 1,
    comments: Array.from(commentStore.entries()),
    votes: Array.from(voteStore.entries()),
    paidAccess: Array.from(paidAccessStore),
    transactions: transactionLedger,
    storyWeaver: Array.from(storyWeaverStore.entries()),
    selfImprovingWorlds: Array.from(selfImprovingWorldStore.entries()),
    bountySubmissions: Array.from(bountySubmissionStore.entries()),
    bountyMiniGames: Array.from(bountyMiniGameStore.entries()),
    bountyGovernance: Array.from(bountyGovernanceStore.entries()),
    savedAt: new Date().toISOString(),
  });
}

export function persistExportSessionStore(): void {
  const now = Date.now();
  const sessions = Array.from(exportSessionStore.values())
    .filter((s) => !isExportSessionExpired(s, now))
    .map((s) => serializeExportSession(s));

  atomicWriteJSON(EXPORT_SESSION_STORE_PATH, {
    version: 1,
    sessions,
    savedAt: new Date().toISOString(),
  });
}

export function persistTokenLedger(): void {
  atomicWriteJSON(TOKEN_LEDGER_PATH, serializeLedger());
}

// ── Initialization ────────────────────────────────────────────────────────────

/**
 * Seed the key registry from env vars on first boot (no keys.json yet).
 * All env key names are treated as founder keys and mapped to a single
 * permanent founder wallet + agent ID. Persists immediately so keys.json
 * exists for subsequent restarts.
 */
function _seedFounderKeysFromEnv(): void {
  const candidates = [
    process.env.HOLOSCRIPT_API_KEY,
    process.env.HOLOSCRIPT_API_KEY,
    process.env.HOLOMESH_API_KEY,
    process.env.COPILOT_HOLOMESH_KEY,
    process.env.GEMINI_HOLOMESH_KEY,
  ].filter((k): k is string => Boolean(k && k.trim()));

  if (candidates.length === 0) return;

  const FOUNDER_WALLET =
    process.env.HOLOSCRIPT_FOUNDER_WALLET || '0x0000000000000000000000000000000000000001';

  for (const key of candidates) {
    const record: KeyRecord = {
      key,
      walletAddress: FOUNDER_WALLET,
      agentId: 'agent_founder',
      agentName: 'Founder',
      scopes: ['*'],
      createdAt: new Date().toISOString(),
      rotationCount: 0,
      lastRotatedAt: null,
      isFounder: true,
    };
    keyRegistry.set(key, record);
  }

  console.info(
    `[KeyRegistry] First boot: seeded ${candidates.length} founder key(s) from env vars`
  );
  persistKeyRegistry();
}

export async function initStores(): Promise<void> {
  // Load Key Registry (must come first — auth depends on it)
  const keyData = readJSON(KEY_REGISTRY_PATH);
  if (keyData?.keys && Array.isArray(keyData.keys) && keyData.keys.length > 0) {
    for (const r of keyData.keys as KeyRecord[]) {
      keyRegistry.set(r.key, r);
    }
    console.info(`[KeyRegistry] Loaded ${keyRegistry.size} key record(s)`);
  } else {
    // First boot: auto-seed from env vars so the server can start immediately
    _seedFounderKeysFromEnv();
  }

  // Load Agents
  const agentData = readJSON(AGENT_STORE_PATH);
  if (agentData?.agents) {
    for (const a of agentData.agents) {
      agentKeyStore.set(a.apiKey, a);
      walletToAgent.set((a.walletAddress || '').toLowerCase(), a);
    }
  }

  // Load Social
  const socialData = readJSON(SOCIAL_STORE_PATH);
  if (socialData) {
    if (socialData.comments) {
      for (const [id, list] of socialData.comments) commentStore.set(id, list);
    }
    if (socialData.votes) {
      for (const [id, list] of socialData.votes) voteStore.set(id, list);
    }
    if (socialData.paidAccess) {
      for (const key of socialData.paidAccess) paidAccessStore.add(key);
    }
    if (socialData.transactions) {
      transactionLedger.push(...socialData.transactions);
    }
    if (socialData.storyWeaver) {
      for (const [id, session] of socialData.storyWeaver) storyWeaverStore.set(id, session);
    }
    if (socialData.selfImprovingWorlds) {
      for (const [id, session] of socialData.selfImprovingWorlds)
        selfImprovingWorldStore.set(id, session);
    }
    if (socialData.bountySubmissions) {
      for (const [id, list] of socialData.bountySubmissions) bountySubmissionStore.set(id, list);
    }
    if (socialData.bountyMiniGames) {
      for (const [id, list] of socialData.bountyMiniGames) bountyMiniGameStore.set(id, list);
    }
    if (socialData.bountyGovernance) {
      for (const [id, proposal] of socialData.bountyGovernance)
        bountyGovernanceStore.set(id, proposal);
    }
  }

  // Load Teams
  // Priority: PostgreSQL (multi-instance) → JSON file (single-instance / migration)
  let teamsLoaded = false;
  if (process.env.DATABASE_URL) {
    try {
      await (teamStore as TeamStore).loadAll();
      teamsLoaded = true;
      console.log('[loadAllStores] teams loaded from PostgreSQL');
      // Reconstruct presence, messages, feed from loaded teams
      for (const t of teamStore.values()) {
        const raw = t as any;
        if (raw.presence) {
          const pMap = new Map();
          for (const p of raw.presence) pMap.set(p.agentId, p);
          teamPresenceStore.set(t.id, pMap);
        }
        if (raw.messages) {
          teamMessageStore.set(t.id, raw.messages);
        }
        const feedRaw = raw.feed as TeamFeedItem[] | undefined;
        if (feedRaw && Array.isArray(feedRaw)) {
          teamFeedStore.set(t.id, feedRaw.slice(-MAX_TEAM_FEED_ITEMS));
        }
        if (t.members) {
          for (const m of t.members) {
            const teams = agentTeamIndex.get(m.agentId) || [];
            if (!teams.includes(t.id)) {
              teams.push(t.id);
              agentTeamIndex.set(m.agentId, teams);
            }
          }
        }
      }
    } catch (e) {
      console.warn('[loadAllStores] PostgreSQL team load failed, falling back to file:', e);
      (teamStore as TeamStore).fallbackToMemory();
      console.warn('[loadAllStores] PostgreSQL team backend disabled for this process');
    }
  }

  if (!teamsLoaded) {
    const teamData = readJSON(TEAM_STORE_PATH);
    if (teamData?.teams) {
      for (const t of teamData.teams) {
        // Reconstitute classes
        if (t.knowledgeMarketplace) {
          try {
            t.knowledgeMarketplace = new KnowledgeMarketplace(t.knowledgeMarketplace);
          } catch {
            /* ignore */
          }
        }
        if (t.bounties) {
          try {
            t.bounties = new BountyManager(t.bounties);
          } catch {
            /* ignore */
          }
        }
        teamStore.set(t.id, t);

        if (t.presence) {
          const pMap = new Map();
          for (const p of t.presence) pMap.set(p.agentId, p);
          teamPresenceStore.set(t.id, pMap);
        }
        if (t.messages) {
          teamMessageStore.set(t.id, t.messages);
        }
        const feedRaw = (t as { feed?: TeamFeedItem[] }).feed;
        if (feedRaw && Array.isArray(feedRaw)) {
          teamFeedStore.set(t.id, feedRaw.slice(-MAX_TEAM_FEED_ITEMS));
        }

        // Re-index members
        if (t.members) {
          for (const m of t.members) {
            const teams = agentTeamIndex.get(m.agentId) || [];
            if (!teams.includes(t.id)) {
              teams.push(t.id);
              agentTeamIndex.set(m.agentId, teams);
            }
          }
        }
      }
    }
  }

  // Load Players — Postgres first, then seed founder if empty
  if (process.env.DATABASE_URL) {
    try {
      await playerStore.loadAll();
      const source = playerStore.usesPostgres ? 'PostgreSQL' : 'memory fallback';
      console.log(`[loadAllStores] players loaded from ${source}: ${playerStore.size}`);
    } catch (e) {
      console.warn('[loadAllStores] PostgreSQL player load failed:', e);
    }
  }
  // Seed the founder as a player on first boot (or after a wipe).
  // Agents querying hololand_list_players will always see at least one real user.
  if (playerStore.size === 0) {
    const founderWallet =
      process.env.HOLOSCRIPT_FOUNDER_WALLET || '0x0000000000000000000000000000000000000001';
    const founderPlayer: StoredPlayer = {
      id: 'player_founder',
      name: 'Joseph',
      walletAddress: founderWallet,
      status: 'active',
      createdAt: new Date().toISOString(),
      modifiedAt: new Date().toISOString(),
    };
    playerStore.set('player_founder', founderPlayer);
    console.info('[PlayerStore] Seeded founder player (player_founder → Joseph)');
  }

  const holoDoorData = readJSON(HOLODOOR_STORE_PATH);
  if (holoDoorData?.policies && typeof holoDoorData.policies === 'object') {
    for (const [tid, pol] of Object.entries(holoDoorData.policies as Record<string, unknown>)) {
      holoDoorPolicyByTeam.set(tid, pol as Record<string, unknown>);
    }
  }
  if (holoDoorData?.events && typeof holoDoorData.events === 'object') {
    for (const [tid, evs] of Object.entries(holoDoorData.events as Record<string, unknown[]>)) {
      const list = Array.isArray(evs) ? (evs as Record<string, unknown>[]) : [];
      holoDoorEventsByTeam.set(tid, list);
    }
  }

  const exportSessionData = readJSON(EXPORT_SESSION_STORE_PATH);
  if (exportSessionData?.sessions && Array.isArray(exportSessionData.sessions)) {
    const now = Date.now();
    for (const raw of exportSessionData.sessions) {
      const session = deserializeExportSession(raw);
      if (!session) continue;
      if (isExportSessionExpired(session, now)) continue;
      exportSessionStore.set(session.sessionId, session);
    }
  }

  // Load Token Ledger (Phase 5 — Tier 2 balance)
  const tokenLedgerData = readJSON(TOKEN_LEDGER_PATH);
  if (tokenLedgerData?.entries) {
    try {
      deserializeLedger(tokenLedgerData);
      console.info(
        `[TokenLedger] Loaded balances for ${Object.keys(tokenLedgerData.entries).length} user(s)`
      );
    } catch (e: unknown) {
      console.warn(
        '[TokenLedger] Failed to load ledger, starting fresh:',
        e instanceof Error ? e.message : String(e)
      );
    }
  }

  // Load CAEL audit, defense, dispatch — PostgreSQL first, then JSONL fallback
  if (process.env.DATABASE_URL) {
    try {
      const auditHandles = await stateStore.listHandles('audit');
      for (const handle of auditHandles) {
        const records = (await stateStore.getAll('audit', handle)) as CaelAuditRecord[];
        const existing = agentAuditStore.get(handle) || [];
        agentAuditStore.set(handle, [...existing, ...records].slice(-MAX_CAEL_RECORDS_PER_AGENT));
      }

      const defenseHandles = await stateStore.listHandles('defense');
      for (const handle of defenseHandles) {
        const config = (await stateStore.get('defense', handle)) as AgentDefenseConfig | undefined;
        if (config) agentDefenseStore.set(handle, config);
      }

      const dispatchHandles = await stateStore.listHandles('dispatch');
      for (const handle of dispatchHandles) {
        const entries = (await stateStore.getAll('dispatch', handle)) as DispatchEntry[];
        const existing = agentDispatchQueue.get(handle) || [];
        agentDispatchQueue.set(
          handle,
          [...existing, ...entries].slice(-MAX_DISPATCH_QUEUE_PER_AGENT)
        );
      }

      console.log(
        `[loadAllStores] state stores from PostgreSQL: audit=${auditHandles.length}, defense=${defenseHandles.length}, dispatch=${dispatchHandles.length}`
      );
    } catch (e) {
      console.warn('[loadAllStores] PostgreSQL state load failed, falling back to JSONL:', e);
    }
  }

  // Fallback / always: JSONL rehydration (catches handles not yet in Postgres)
  loadCaelAuditFromDisk();
  loadAgentDefenseFromDisk();
}
